import { Search, Sparkles, Layers } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CategoryCard from '@/components/CategoryCard';
import ToolCard from '@/components/ToolCard';
import { categories, tools, getToolsByCategory } from '@/data/tools';

const Index = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTools = tools.filter(
    (tool) =>
      tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden border-b border-border/50 bg-gradient-to-b from-primary/5 via-background to-background">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />
          <div className="container relative mx-auto px-4 py-16 md:py-24">
            <div className="mx-auto max-w-3xl text-center">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
                <Sparkles className="h-4 w-4" />
                All-in-One Multitools Platform
              </div>
              <h1 className="mb-6 text-4xl font-bold tracking-tight text-foreground md:text-5xl lg:text-6xl">
                Simplify Your Daily Tasks with{' '}
                <span className="bg-gradient-to-r from-primary to-accent-foreground bg-clip-text text-transparent">
                  MultiChoice
                </span>
              </h1>
              <p className="mb-8 text-lg text-muted-foreground md:text-xl">
                Access a powerful collection of tools designed to boost your productivity,
                manage your finances, and simplify everyday tasks — all in one place.
              </p>

              <div className="relative mx-auto max-w-xl">
                <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search for tools..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-14 bg-card pl-12 text-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section id="categories" className="container mx-auto px-4 py-16">
          <div className="mb-10 text-center">
            <h2 className="mb-3 text-3xl font-bold text-foreground">Browse by Category</h2>
            <p className="text-muted-foreground">
              Find the right tool for any task across our organized categories
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((category, index) => (
              <CategoryCard
                key={category.id}
                category={category}
                toolCount={getToolsByCategory(category.id).length}
                index={index}
              />
            ))}
          </div>
        </section>

        {/* All Tools Section */}
        <section id="all-tools" className="container mx-auto px-4 py-16">
          <div className="mb-10 flex items-center justify-between">
            <div>
              <h2 className="mb-2 text-3xl font-bold text-foreground">
                {searchQuery ? 'Search Results' : 'All Tools'}
              </h2>
              <p className="text-muted-foreground">
                {filteredTools.length} tool{filteredTools.length !== 1 ? 's' : ''} available
              </p>
            </div>
          </div>

          {filteredTools.length > 0 ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredTools.map((tool, index) => (
                <ToolCard key={tool.id} tool={tool} index={index} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Layers className="mb-4 h-16 w-16 text-muted-foreground/50" />
              <h3 className="mb-2 text-xl font-semibold text-foreground">No tools found</h3>
              <p className="text-muted-foreground">
                Try adjusting your search query to find what you're looking for.
              </p>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
